import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, BedDouble, Phone, User, Home, ClipboardList, PackagePlus,
  Pencil, IdCard, HeartPulse, Users2, FileSignature, Star, CalendarClock, TrendingUp,
  FileText, Upload, Trash2, Eye, Stethoscope, Paperclip, CalendarDays,
  UserX, UserCheck, FileBarChart2, FolderArchive, Mail, Landmark, Share2, Tag,
} from "lucide-react";
import { useApp, formatDate, formatBRL } from "../context/AppContext";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import ResidenteFormDialog from "../components/ResidenteFormDialog";
import { calcularRenovacoes, contratoResumo, dataBR } from "../utils/contrato";
import { gerarFichaPDF } from "../utils/fichaPdf";
import { gerarRelatorioClinicoPDF, gerarRelatorioFinanceiroPDF } from "../utils/relatorioCompletoPdf";
import { toast } from "sonner";
import { RescisaoAprovarDialog, isFaturaRescisao, BoletoLink } from "../components/RescisaoAprovarDialog";

const PAGOS = ["RECEIVED", "CONFIRMED", "PAID", "RECEIVED_IN_CASH"];
const RescisaoCard = ({ res, fatura, boleto }) => {
  const [open, setOpen] = useState(false);
  const itens = [...(fatura.extras || []), ...(fatura.manual || [])];
  const emitido = !!boleto;
  const pagoAsaas = PAGOS.includes(String(boleto?.status || "").toUpperCase());
  return (
    <div className="bg-white rounded-xl border border-orange-200 shadow-sm" data-testid="rescisao-card">
      <div className="px-5 py-4 border-b border-orange-100 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-semibold text-slate-800 flex items-center gap-2"><FileBarChart2 className="w-4 h-4 text-orange-600" /> Fatura de rescisão · {fatura.mes}</h2>
          <p className="text-xs text-slate-400 mt-0.5">13º proporcional e consumos pendentes até a inativação. Revise e aprove para emitir o boleto no Asaas.</p>
        </div>
        {fatura.paga ? (
          <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-1 rounded inline-flex items-center gap-1.5" data-testid="rescisao-status">
            <UserCheck className="w-3.5 h-3.5" /> Paga{fatura.dataPagamento ? ` em ${formatDate(fatura.dataPagamento)}` : ""}{pagoAsaas ? " · baixa automática (Asaas)" : ""}
          </span>
        ) : emitido ? (
          <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-1 rounded inline-flex items-center gap-2" data-testid="rescisao-status">
            Boleto emitido{boleto.criadoEm ? ` em ${formatDate(String(boleto.criadoEm).slice(0, 10))}` : ""} <BoletoLink bol={boleto} testid="rescisao-ver-boleto" />
          </span>
        ) : (
          <span className="text-xs font-semibold text-orange-700 bg-orange-50 px-2 py-1 rounded" data-testid="rescisao-status">Aguardando aprovação</span>
        )}
      </div>
      <div className="p-5 space-y-3">
        <table className="w-full text-sm"><tbody className="divide-y divide-slate-100">
          {itens.map((it, i) => (<tr key={it.id || i}><td className="py-1.5 text-slate-700">{it.descricao}</td><td className="py-1.5 text-right font-medium text-slate-700">{formatBRL(it.valor)}</td></tr>))}
          {(fatura.itens || []).map((c) => (<tr key={c.id}><td className="py-1.5 text-slate-700">Consumo {formatDate(c.data)} · {c.qtdEmpresa} un</td><td className="py-1.5 text-right font-medium text-slate-700">{formatBRL(c.valorCobrado)}</td></tr>))}
        </tbody></table>
        <div className="flex items-center justify-between pt-3 border-t border-slate-200 flex-wrap gap-2">
          <span className="font-semibold text-slate-800" data-testid="rescisao-card-total">Total: {formatBRL(fatura.total)}</span>
          {!fatura.paga && (
            <Button className={`gap-2 ${emitido ? "" : "bg-orange-600 hover:bg-orange-700"}`} variant={emitido ? "outline" : "default"}
              onClick={() => { if (!emitido || window.confirm("Já existe boleto para esta rescisão. Emitir novamente criará uma NOVA cobrança no Asaas. Continuar?")) setOpen(true); }}
              data-testid="rescisao-aprovar-btn">
              <Landmark className="w-4 h-4" /> {emitido ? "Emitir novamente" : "Aprovar e emitir boleto"}
            </Button>
          )}
        </div>
      </div>
      <RescisaoAprovarDialog fatura={fatura} residente={res} open={open} onClose={() => setOpen(false)} />
    </div>
  );
};

const grauLabel = { "1": "Grau I - Independente", "2": "Grau II - Dependência parcial", "3": "Grau III - Dependência total" };

const Info = ({ label, value }) => (
  <div>
    <p className="text-xs text-slate-400">{label}</p>
    <p className="text-sm text-slate-700 font-medium">{value || "-"}</p>
  </div>
);

const fmtBytes = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : n >= 1024 ? `${Math.round(n / 1024)} KB` : `${n} B`);
const _thumbCache = new Map();
const DocThumb = ({ rid, doc, fetcher, onClick, tone }) => {
  const [url, setUrl] = useState(() => _thumbCache.get(doc.id) || "");
  const [erro, setErro] = useState(false);
  useEffect(() => {
    if (!doc.thumb || url) return;
    let ativo = true;
    fetcher(rid, doc.id).then((blob) => {
      const u = URL.createObjectURL(blob); _thumbCache.set(doc.id, u); if (ativo) setUrl(u);
    }).catch(() => ativo && setErro(true));
    return () => { ativo = false; };
  }, [rid, doc.id, doc.thumb, url, fetcher]);
  const fallback = doc.tipo === "pdf" ? "bg-red-50 text-red-500" : tone;
  return (
    <button type="button" onClick={onClick} title="Abrir" className={`relative w-full h-28 rounded-t-lg overflow-hidden flex items-center justify-center ${url ? "bg-slate-100" : fallback}`} data-testid={`thumb-doc-${doc.id}`}>
      {url && !erro ? <img src={url} alt={doc.nome} className="w-full h-full object-cover object-top" loading="lazy" />
        : doc.thumb && !erro ? <div className="w-6 h-6 rounded-full border-2 border-slate-300 border-t-slate-500 animate-spin" />
        : <FileText className="w-8 h-8" />}
      {doc.tipo === "pdf" && <span className="absolute top-1.5 left-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-600 text-white">PDF</span>}
    </button>
  );
};

const DOC_CLASSES = ["RG", "CPF", "Exame", "Receita", "Laudo", "Contrato", "Vacina", "Comprovante", "Outros"];
const ClasseChips = ({ docs, ativo, onChange, testId }) => {
  const cont = docs.reduce((m, d) => { const c = d.classe || "Outros"; m[c] = (m[c] || 0) + 1; return m; }, {});
  const classes = DOC_CLASSES.filter((c) => cont[c]);
  if (docs.length < 2 || classes.length < 2) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mb-4" data-testid={testId}>
      <button type="button" onClick={() => onChange("")} className={`text-xs px-2.5 py-1 rounded-full border ${!ativo ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`} data-testid={`${testId}-todos`}>Todos ({docs.length})</button>
      {classes.map((c) => (
        <button key={c} type="button" onClick={() => onChange(ativo === c ? "" : c)} className={`text-xs px-2.5 py-1 rounded-full border ${ativo === c ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`} data-testid={`${testId}-${c}`}>{c} ({cont[c]})</button>
      ))}
    </div>
  );
};

const DropZone = ({ ativo, onFiles, children, className = "", testId }) => {
  const [over, setOver] = useState(false);
  if (!ativo) return <div className={className}>{children}</div>;
  return (
    <div className={`${className} relative transition-colors ${over ? "ring-2 ring-blue-400 ring-offset-2 bg-blue-50/40" : ""}`}
      onDragOver={(e) => { e.preventDefault(); if (!over) setOver(true); }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOver(false); }}
      onDrop={(e) => { e.preventDefault(); setOver(false); const fs = Array.from(e.dataTransfer.files || []).filter((f) => f.type === "application/pdf" || f.type.startsWith("image/")); if (fs.length) onFiles(fs); }}
      data-testid={testId}>
      {children}
      {over && <div className="absolute inset-0 rounded-xl border-2 border-dashed border-blue-400 bg-blue-50/70 flex items-center justify-center pointer-events-none z-10"><span className="text-sm font-medium text-blue-700 flex items-center gap-2"><Upload className="w-4 h-4" /> Solte os arquivos para anexar</span></div>}
    </div>
  );
};
const UploadBar = ({ prog }) => (
  <div className="mb-4 rounded-lg border border-blue-100 bg-blue-50/60 px-4 py-3" data-testid="upload-progress">
    <div className="flex items-center justify-between text-xs text-slate-600 mb-1.5">
      <span className="truncate">Enviando {prog.i} de {prog.n}: <b>{prog.nome}</b></span>
      <span className="font-semibold text-blue-700 ml-3">{prog.pct}%</span>
    </div>
    <div className="h-1.5 rounded-full bg-blue-100 overflow-hidden"><div className="h-full bg-blue-600 transition-[width] duration-200" style={{ width: `${prog.pct}%` }} /></div>
  </div>
);

const ResidenteDetalhe = () => {
  const { id } = useParams();
  const {
    getResidente, estoqueResidente, consumos, fornecimentos, getProduto, faturas, salvarResidente,
    uploadDocumento, getDocumentoBlob, getDocumentoThumb, deleteDocumento, patchDocumento, isAdmin, refresh, auth, data, podeAcao,
    salvarResidenteServico, excluirResidenteServico,
    inativarResidente, reativarResidente, getProntuario, empresa,
    baixarContrato, enviarContratoEmail, baixarDocumento, enviarDocumentoEmail, boletos,
  } = useApp();
  const permsCustom = auth?.permissoes && typeof auth.permissoes === "object" && Object.keys(auth.permissoes).length > 0 ? auth.permissoes : null;
  const arq = permsCustom?.arquivos;
  const podeDocumentos = isAdmin || (arq ? !!arq.ver : auth?.perfil === "enfermeiro");
  const podeEnviarDocs = isAdmin || (arq ? !!arq.criar : auth?.perfil === "enfermeiro");
  const podeExcluirDocs = isAdmin || (arq ? !!arq.excluir : auth?.perfil === "enfermeiro");
  const podeEditarDocs = isAdmin || (arq ? !!arq.editar : false);
  const podeImprimir = isAdmin || (permsCustom ? !!permsCustom.documentos?.ver : false);
  const [novoServ, setNovoServ] = useState({ servicoExtraId: "", nome: "", valor: "", recorrente: true });
  const [inativarOpen, setInativarOpen] = useState(false);
  const [motivoInativar, setMotivoInativar] = useState("");
  const [dataInativar, setDataInativar] = useState(new Date().toISOString().slice(0, 10));
  const [gerarFaturaRescisao, setGerarFaturaRescisao] = useState(true);
  const [inativando, setInativando] = useState(false);
  const [gerando, setGerando] = useState(false);
  const [contratoOpen, setContratoOpen] = useState(false);
  const [contratoEmail, setContratoEmail] = useState("");
  const [enviandoContrato, setEnviandoContrato] = useState(false);
  const [emailDoc, setEmailDoc] = useState(null); // {key,label}
  const [emailDocValor, setEmailDocValor] = useState("");
  const [enviandoDoc, setEnviandoDoc] = useState(false);
  const [relClinicoOpen, setRelClinicoOpen] = useState(false);
  const [relInicio, setRelInicio] = useState("");
  const [relFim, setRelFim] = useState("");
  const [edit, setEdit] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingArq, setUploadingArq] = useState(false);
  const [uploadProg, setUploadProg] = useState(null);
  const [excluindoDoc, setExcluindoDoc] = useState(null);
  const [docEdit, setDocEdit] = useState(null);
  const [salvandoDoc, setSalvandoDoc] = useState(false);
  const [filtroClasse, setFiltroClasse] = useState({ documento: "", arquivo: "" });
  const fileRef = useRef(null);
  const arqRef = useRef(null);

  const res = getResidente(id);
  if (!res) return <div className="text-slate-500">Residente não encontrado. <Link to="/residentes" className="text-blue-600">Voltar</Link></div>;

  const estoque = estoqueResidente[id] || {};
  const itensEstoque = Object.entries(estoque).filter(([, q]) => q > 0);
  const consumosRes = consumos.filter((c) => c.residenteId === id);
  const fornRes = fornecimentos.filter((f) => f.residenteId === id);
  const faturasRes = faturas.filter((f) => f.residenteId === id);
  const totalAberto = faturasRes.filter((f) => !f.paga).reduce((s, f) => s + f.total, 0);
  const faturaRescisao = faturasRes.find((f) => isFaturaRescisao(f) && !f.cancelada);

  const familiares = res.familiares || [];
  const resumo = contratoResumo(res.dataInicioContrato, res.valorMensalidade);
  const renovacoes = calcularRenovacoes(res.dataInicioContrato, res.valorMensalidade);
  const documentos = res.documentos || [];
  const docsGeraisAll = documentos.filter((d) => (d.categoria || "documento") !== "arquivo");
  const docsArquivoAll = documentos.filter((d) => (d.categoria || "documento") === "arquivo");
  const docsGerais = filtroClasse.documento ? docsGeraisAll.filter((d) => (d.classe || "Outros") === filtroClasse.documento) : docsGeraisAll;
  const docsArquivo = filtroClasse.arquivo ? docsArquivoAll.filter((d) => (d.classe || "Outros") === filtroClasse.arquivo) : docsArquivoAll;
  const servicosRes = (data?.residenteServicos || []).filter((s) => s.residenteId === id);
  const catalogo = (data?.servicosExtras || []);
  const addServico = async () => {
    if (!novoServ.nome.trim() || novoServ.valor === "") return;
    await salvarResidenteServico(id, { ...novoServ, valor: Number(novoServ.valor) });
    setNovoServ({ servicoExtraId: "", nome: "", valor: "", recorrente: true });
  };
  const pickServico = (sxid) => {
    const c = catalogo.find((x) => x.id === sxid);
    setNovoServ((n) => ({ ...n, servicoExtraId: sxid, nome: c ? c.nome : n.nome, valor: c ? c.valor : n.valor }));
  };
  const perLabel = { mensal: "Mensal", trimestral: "Trimestral", semestral: "Semestral", anual: "Anual" };

  const doUpload = async (files, categoria) => {
    let ok = 0;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploadProg({ i: i + 1, n: files.length, pct: 0, nome: file.name });
      try {
        await uploadDocumento(id, file, categoria, (pct) => setUploadProg((p) => (p ? { ...p, pct } : p)));
        ok++;
      } catch (e) {
        toast.error(`Falha ao enviar "${file.name}": ${e?.response?.data?.detail || e.message || "erro"}`);
      }
    }
    setUploadProg(null);
    if (ok) toast.success(ok === 1 ? "Arquivo anexado." : `${ok} arquivos anexados.`);
  };

  const removerDoc = async (docId) => {
    if (!window.confirm("Excluir este documento?")) return;
    setExcluindoDoc(docId);
    try { await deleteDocumento(id, docId); toast.success("Documento excluído."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Falha ao excluir."); }
    finally { setExcluindoDoc(null); }
  };

  const salvarDocEdit = async () => {
    if (!docEdit?.nome?.trim()) return toast.error("Informe o nome do documento.");
    setSalvandoDoc(true);
    try {
      await patchDocumento(id, docEdit.id, { nome: docEdit.nome.trim(), classe: docEdit.classe || "", compartilhado: !!docEdit.compartilhado });
      toast.success("Documento atualizado."); setDocEdit(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao salvar."); }
    finally { setSalvandoDoc(false); }
  };
  const toggleCompartilhar = async (d) => {
    try { await patchDocumento(id, d.id, { compartilhado: !d.compartilhado }); toast.success(!d.compartilhado ? "Compartilhado com a família." : "Compartilhamento removido."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Falha ao atualizar."); }
  };

  const doUploadDrop = async (files, categoria) => {
    const setFlag = categoria === "arquivo" ? setUploadingArq : setUploading;
    setFlag(true);
    await doUpload(files, categoria);
    setFlag(false);
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    await doUpload(files, "documento");
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleUploadArquivo = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingArq(true);
    await doUpload(files, "arquivo");
    setUploadingArq(false);
    if (arqRef.current) arqRef.current.value = "";
  };

  const handleView = async (docId) => {
    const w = window.open("", "_blank");
    if (w) w.document.write("<p style='font-family:sans-serif;color:#64748b;padding:24px'>Carregando documento...</p>");
    try {
      const blob = await getDocumentoBlob(id, docId);
      const url = URL.createObjectURL(blob);
      if (w) w.location.href = url; else window.open(url, "_blank");
    } catch (e) {
      if (w) w.close();
      toast.error(e?.response?.data?.detail || "Não foi possível abrir o documento.");
    }
  };

  const confirmarInativar = async () => {
    if (!motivoInativar.trim()) return;
    setInativando(true);
    try {
      const upd = await inativarResidente(id, motivoInativar.trim(), {
        data: dataInativar, gerarFatura: gerarFaturaRescisao,
      });
      const rec = upd?.faturaRescisao;
      if (gerarFaturaRescisao) {
        if (rec && rec.total > 0) toast.success(`Fatura de rescisão criada em ${rec.mes}: ${rec.total.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })} (${rec.itens.length} item(ns)).`);
        else toast.info("Inativado. Nenhum valor de rescisão a cobrar (sem 13º ou consumos pendentes).");
      } else {
        toast.success("Residente inativado.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao inativar.");
    } finally {
      setInativando(false);
      setInativarOpen(false);
      setMotivoInativar("");
    }
  };

  const handleRelatorioCompleto = async () => {
    setGerando(true);
    try {
      const pront = await getProntuario(id);
      let registros = pront?.registros || [];
      if (relInicio) registros = registros.filter((r) => (r.data || "").slice(0, 10) >= relInicio);
      if (relFim) registros = registros.filter((r) => (r.data || "").slice(0, 10) <= relFim);
      gerarRelatorioClinicoPDF({ empresa, residente: res, registros });
      setRelClinicoOpen(false);
    } finally {
      setGerando(false);
    }
  };

  const handleRelatorioFinanceiro = () => {
    gerarRelatorioFinanceiroPDF({ empresa, residente: res, faturas: faturasRes });
  };

  const abrirEnviarContrato = () => {
    const fin = (res.familiares || []).find((f) => f.responsavelFinanceiro) || (res.familiares || [])[0];
    setContratoEmail(fin?.email || "");
    setContratoOpen(true);
  };
  const handleEnviarContrato = async () => {
    setEnviandoContrato(true);
    try {
      const r = await enviarContratoEmail(res.id, contratoEmail);
      toast.success(`Contrato enviado para ${r.para}`);
      setContratoOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao enviar o contrato.");
    } finally {
      setEnviandoContrato(false);
    }
  };

  const ativo = (res.status || "Ativo") === "Ativo";

  const abrirEnviarDoc = (key, label) => {
    const fin = (res.familiares || []).find((f) => f.responsavelFinanceiro) || (res.familiares || [])[0];
    setEmailDocValor(fin?.email || "");
    setEmailDoc({ key, label });
  };
  const handleEnviarDoc = async () => {
    if (!emailDoc) return;
    setEnviandoDoc(true);
    try {
      const r = await enviarDocumentoEmail(res.id, emailDoc.key, emailDocValor);
      if (r.status === "enviado") toast.success(`${emailDoc.label} enviado para ${r.para}`);
      else toast.error(r.erro || "Não foi possível enviar o e-mail.");
      setEmailDoc(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao enviar o documento.");
    } finally {
      setEnviandoDoc(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Link to="/residentes" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700">
          <ArrowLeft className="w-4 h-4" /> Voltar para Residentes
        </Link>
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={`/residentes/${id}/prontuario`}>
            <Button variant="outline" className="gap-2"><Stethoscope className="w-4 h-4" /> Prontuário</Button>
          </Link>
          {(isAdmin || podeAcao("residentes", "editar")) && (<>
            <Button variant="outline" className="gap-2" onClick={() => setRelClinicoOpen(true)} data-testid="relatorio-completo-btn">
              <FileBarChart2 className="w-4 h-4" /> Relatório Clínico
            </Button>
            <Button variant="outline" className="gap-2" onClick={handleRelatorioFinanceiro} data-testid="relatorio-financeiro-btn">
              <FileBarChart2 className="w-4 h-4" /> Relatório Financeiro
            </Button>
            <Button variant="outline" className="gap-2" onClick={() => gerarFichaPDF({ empresa, residente: res })} data-testid="imprimir-ficha-btn">
              <FileText className="w-4 h-4" /> Imprimir Ficha
            </Button>
            <Button variant="outline" className="gap-2" onClick={() => baixarContrato(res.id)} data-testid="imprimir-contrato-btn">
              <FileSignature className="w-4 h-4" /> Imprimir Contrato
            </Button>
            <Button variant="outline" className="gap-2" onClick={abrirEnviarContrato} data-testid="enviar-contrato-btn">
              <FileSignature className="w-4 h-4" /> Enviar Contrato
            </Button>
            <Button variant="outline" className="gap-2" onClick={() => setEdit(true)} data-testid="editar-residente-btn">
              <Pencil className="w-4 h-4" /> Editar
            </Button>
            {ativo ? (
              <Button variant="outline" className="gap-2 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700" onClick={() => setInativarOpen(true)} data-testid="inativar-residente-btn">
                <UserX className="w-4 h-4" /> Inativar
              </Button>
            ) : (
              <Button variant="outline" className="gap-2 text-emerald-600 border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700" onClick={() => reativarResidente(id)} data-testid="reativar-residente-btn">
                <UserCheck className="w-4 h-4" /> Reativar
              </Button>
            )}
          </>)}
        </div>
      </div>

      {/* Header */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-5">
          {res.foto ? (
            <img src={res.foto} alt={res.nome} className="w-16 h-16 rounded-2xl object-cover" />
          ) : (
            <div className="w-16 h-16 rounded-2xl bg-blue-600 text-white flex items-center justify-center text-xl font-bold">
              {res.nome.split(" ").map((n) => n[0]).slice(0, 2).join("")}
            </div>
          )}
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold text-slate-800">{res.nome}</h1>
              {!ativo && (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-50 px-2 py-0.5 rounded" data-testid="status-inativo-badge">
                  <UserX className="w-3.5 h-3.5" /> Inativo
                </span>
              )}
            </div>
            {!ativo && res.motivoInativacao && (
              <p className="text-xs text-red-500 mt-1" data-testid="motivo-inativacao">
                Inativado em {formatDate(res.dataInativacao)} · {res.motivoInativacao}
              </p>
            )}
            <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm text-slate-500">
              <span className="flex items-center gap-1.5"><BedDouble className="w-4 h-4" /> Quarto {res.quarto || "-"}</span>
              <span className="flex items-center gap-1.5"><HeartPulse className="w-4 h-4" /> {grauLabel[res.grauDependencia] || "-"}</span>
              <span className="flex items-center gap-1.5"><IdCard className="w-4 h-4" /> CPF {res.cpf || "-"}</span>
              <span>Internação: {formatDate(res.dataInternacao)}</span>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-400">{isAdmin ? "Em aberto (faturas)" : "Grau de dependência"}</p>
            <p className="text-2xl font-bold text-slate-800">{isAdmin ? formatBRL(totalAberto) : (res.grauDependencia ? `Grau ${res.grauDependencia}` : "-")}</p>
          </div>
        </div>
      </div>

      {isAdmin && faturaRescisao && (
        <RescisaoCard res={res} fatura={faturaRescisao} boleto={boletos?.[faturaRescisao.key]} />
      )}

      {/* Imprimir Documentos (administrador ou permissão "documentos") */}
      {podeImprimir && (
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm" data-testid="imprimir-documentos-card">
        <div className="px-5 py-4 border-b border-slate-100">
          <h2 className="font-semibold text-slate-800 flex items-center gap-2"><FileText className="w-4 h-4 text-blue-600" /> Imprimir Documentos</h2>
          <p className="text-xs text-slate-400 mt-0.5">Gera o PDF preenchido com os dados deste residente. Edite os modelos em Configurações → Documentos.</p>
        </div>
        <div className="p-5 flex flex-wrap gap-2">
          {[
            ["contrato", "Contrato", FileSignature],
            ["ficha_cadastral", "Ficha Cadastral", FileText],
            ["ficha_internacao", "Ficha de Internação", FileText],
            ["prescricao_contencao", "Autorização de Contenção", FileText],
            ["uso_imagem", "Uso de Imagem", FileText],
            ["declaracao_autorizadora", "Declaração Autorizadora", FileText],
          ].map(([k, label, Icon]) => (
            <div key={k} className="flex items-center rounded-lg border border-slate-200 overflow-hidden bg-white">
              <button
                type="button"
                data-testid={`doc-print-${k}`}
                className="px-3 py-2 text-sm flex items-center gap-2 text-slate-700 hover:bg-slate-50"
                onClick={async () => {
                  try {
                    if (k === "contrato") await baixarContrato(res.id);
                    else await baixarDocumento(res.id, k);
                  } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao gerar o documento."); }
                }}
              >
                <Icon className={`w-4 h-4 ${k === "contrato" ? "text-blue-600" : "text-slate-500"}`} /> {label}
              </button>
              <button
                type="button"
                data-testid={`doc-email-${k}`}
                title={`Enviar ${label} por e-mail`}
                className="px-2.5 py-2 border-l border-slate-200 text-slate-500 hover:bg-blue-50 hover:text-blue-600"
                onClick={() => (k === "contrato" ? abrirEnviarContrato() : abrirEnviarDoc(k, label))}
              >
                <Mail className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
      )}

      {/* Contrato (administrador ou permissão "documentos") */}
      {podeImprimir && (
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm">
        <div className="px-5 py-4 border-b border-slate-100">
          <h2 className="font-semibold text-slate-800 flex items-center gap-2"><FileSignature className="w-4 h-4 text-blue-600" /> Contrato & Mensalidade</h2>
        </div>
        <div className="p-5 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="grid grid-cols-2 gap-4 lg:col-span-1">
            <Info label="Início do contrato" value={formatDate(res.dataInicioContrato)} />
            <div>
              <p className="text-xs text-slate-400">Mensalidade vigente</p>
              <p className="text-lg font-bold text-slate-800">{formatBRL(resumo ? resumo.atual.valor : res.valorMensalidade)}</p>
            </div>
            <Info label="Valor informado (atual)" value={formatBRL(res.valorMensalidade)} />
            {Number(res.descontoPercentual) > 0 && <Info label="Desconto na mensalidade" value={`${Number(res.descontoPercentual).toLocaleString("pt-BR")}%`} />}
            <div data-testid="ultimo-reajuste">
              <p className="text-xs text-slate-400">Último reajuste automático</p>
              {(res.historicoReajustes || []).length > 0 ? (() => { const u = res.historicoReajustes[res.historicoReajustes.length - 1]; return (
                <p className="text-sm text-slate-700 font-medium">{formatDate(u.data)} · {formatBRL(u.de)} → {formatBRL(u.para)} (+{u.percentual}%)</p>
              ); })() : <p className="text-sm text-slate-500">Nenhum ainda{res.proximoReajuste ? ` · próximo em ${formatDate(res.proximoReajuste)}` : ""}</p>}
            </div>
            <div>
              <p className="text-xs text-slate-400">Próxima renovação</p>
              <p className="text-sm text-slate-700 font-medium">
                {resumo && resumo.proxima ? `${dataBR(resumo.proxima.inicio)} · ${formatBRL(resumo.proxima.valor)}` : "-"}
              </p>
            </div>
            <Info label="Dia de pagamento" value={res.diaPagamento ? `Todo dia ${res.diaPagamento}` : "-"} />
            <Info label="Periodicidade" value={perLabel[res.periodicidadePagamento] || "-"} />
            <Info label="Forma de cobrança" value={res.formaCobranca === "pix" ? "Pix manual (sem boleto automático)" : "Boleto automático (Asaas)"} />
          </div>
          <div className="lg:col-span-2">
            <p className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" /> Renovações (reajuste 8% a cada 12 meses)</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-400 border-b border-slate-100">
                    <th className="py-2 font-medium">Ciclo</th>
                    <th className="py-2 font-medium">Período</th>
                    <th className="py-2 font-medium text-right">Mensalidade</th>
                    <th className="py-2 font-medium text-center">Situação</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {renovacoes.map((r) => (
                    <tr key={r.ciclo} className={r.vigente ? "bg-blue-50/40" : ""}>
                      <td className="py-2.5 text-slate-600">{r.ciclo}º</td>
                      <td className="py-2.5 text-slate-600">{dataBR(r.inicio)} – {dataBR(r.fim)}</td>
                      <td className="py-2.5 text-right font-medium text-slate-700">{formatBRL(r.valor)}</td>
                      <td className="py-2.5 text-center">
                        {r.vigente ? <span className="text-[11px] font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded">Vigente</span>
                          : r.futuro ? <span className="text-[11px] font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded">Futuro</span>
                          : <span className="text-[11px] font-semibold text-slate-400">Encerrado</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Resumo clínico (somente leitura) */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm" data-testid="ficha-resumo-clinico">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h2 className="font-semibold text-slate-800 flex items-center gap-2"><HeartPulse className="w-4 h-4 text-blue-600" /> Resumo Clínico</h2>
          <Link to={`/residentes/${res.id}/prontuario`} className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700"><Stethoscope className="w-4 h-4" /> Abrir prontuário</Link>
        </div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[["Tipo sanguíneo", res.tipoSanguineo], ["Dieta", res.dieta], ["Mobilidade", res.mobilidade], ["Alergias", res.alergias],
            ["Diagnósticos (CID)", res.diagnosticos], ["Condições crônicas", res.condicoesCronicas],
            ["Plano de cuidados", res.planoCuidados], ["Observações clínicas", res.observacoesClinicas]].map(([label, val]) => (
            <div key={label}><p className="text-xs font-medium text-slate-400">{label}</p><p className="text-sm text-slate-700 mt-0.5">{val || "—"}</p></div>
          ))}
        </div>
      </div>

      {/* Dados cadastrais */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm">
          <div className="px-5 py-4 border-b border-slate-100">
            <h2 className="font-semibold text-slate-800 flex items-center gap-2"><User className="w-4 h-4 text-blue-600" /> Dados Pessoais</h2>
          </div>
          <div className="p-5 grid grid-cols-2 gap-4">
            <Info label="Nacionalidade" value={res.nacionalidade} />
            <Info label="Naturalidade" value={res.naturalidade} />
            <Info label="Sexo" value={res.sexo} />
            <Info label="Nome do Pai" value={res.nomePai} />
            <Info label="Nome da Mãe" value={res.nomeMae} />
          </div>
        </div>

        {isAdmin && (
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm">
          <div className="px-5 py-4 border-b border-slate-100">
            <h2 className="font-semibold text-slate-800 flex items-center gap-2"><IdCard className="w-4 h-4 text-blue-600" /> Documentos & Convênio</h2>
          </div>
          <div className="p-5 grid grid-cols-2 gap-4">
            <Info label="CPF" value={res.cpf} />
            <Info label="CNS" value={res.cns} />
            <Info label="RG" value={res.rg} />
            <Info label="Órgão Emissor" value={res.orgaoEmissor} />
            <Info label="Data de Emissão" value={res.dataEmissaoRg ? formatDate(res.dataEmissaoRg) : "-"} />
            <Info label="Convênio" value={res.convenio} />
            <Info label="Carteirinha" value={res.carteirinhaConvenio} />
          </div>
        </div>
        )}
      </div>

      {/* Familiares */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm">
        <div className="px-5 py-4 border-b border-slate-100">
          <h2 className="font-semibold text-slate-800 flex items-center gap-2"><Users2 className="w-4 h-4 text-blue-600" /> Familiares</h2>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          {familiares.length === 0 && <p className="text-sm text-slate-400">Nenhum familiar cadastrado.</p>}
          {familiares.map((f, fi) => (
            <div key={f.id || fi} className="rounded-xl border border-slate-100 p-4">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-slate-800">{f.nome}</p>
                <span className="text-xs text-slate-400">{f.parentesco}</span>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {f.responsavelFinanceiro && <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded"><Star className="w-3 h-3" /> Resp. financeiro</span>}
                {f.contatoEmergencia && <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-600 bg-red-50 px-2 py-0.5 rounded"><Phone className="w-3 h-3" /> Emergência</span>}
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
                <Info label="CPF" value={f.cpf} />
                <Info label="RG" value={f.rg} />
                <Info label="Telefone" value={f.telefone} />
                <Info label="E-mail" value={f.email} />
              </div>
              {(f.endLogradouro || f.endCidade) && (
                <p className="text-xs text-slate-500 mt-2">
                  {[f.endLogradouro, f.endNumero, f.endBairro, f.endCidade, f.endUf].filter(Boolean).join(", ")}
                  {f.endCep ? ` · CEP ${f.endCep}` : ""}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Serviços cobrados na mensalidade (somente administrador) */}
      {isAdmin && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-4" data-testid="residente-servicos-section">
          <h2 className="font-semibold text-slate-800 flex items-center gap-2"><IdCard className="w-4 h-4 text-emerald-600" /> Serviços cobrados na mensalidade</h2>
          <p className="text-xs text-slate-400">Recorrente = cobrado todo mês na fatura (com reajuste anual de 8% do contrato). Avulso = cobrado apenas na próxima fatura.</p>
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end">
            <div className="sm:col-span-4">
              <label className="text-xs font-medium text-slate-500">Serviço do catálogo</label>
              <select className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" value={novoServ.servicoExtraId} onChange={(e) => pickServico(e.target.value)} data-testid="serv-catalogo">
                <option value="">— selecionar / digitar —</option>
                {catalogo.map((c) => <option key={c.id} value={c.id}>{c.nome} ({formatBRL(c.valor)})</option>)}
              </select>
            </div>
            <div className="sm:col-span-3">
              <label className="text-xs font-medium text-slate-500">Descrição</label>
              <input className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" value={novoServ.nome} onChange={(e) => setNovoServ((n) => ({ ...n, nome: e.target.value }))} placeholder="Ex.: Fisioterapia" data-testid="serv-nome" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-medium text-slate-500">Valor (R$)</label>
              <input type="number" step="0.01" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" value={novoServ.valor} onChange={(e) => setNovoServ((n) => ({ ...n, valor: e.target.value }))} data-testid="serv-valor" />
            </div>
            <div className="sm:col-span-2">
              <label className="flex items-center gap-2 text-sm text-slate-600 py-2">
                <input type="checkbox" className="w-4 h-4 accent-emerald-600" checked={novoServ.recorrente} onChange={(e) => setNovoServ((n) => ({ ...n, recorrente: e.target.checked }))} data-testid="serv-recorrente" /> Recorrente
              </label>
            </div>
            <div className="sm:col-span-1">
              <button onClick={addServico} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-3 py-2 text-sm font-medium" data-testid="serv-add">Adicionar</button>
            </div>
          </div>
          <div className="divide-y divide-slate-100 border border-slate-100 rounded-xl">
            {servicosRes.length === 0 && <p className="text-sm text-slate-400 px-4 py-3">Nenhum serviço vinculado.</p>}
            {servicosRes.map((s) => (
              <div key={s.id} className="flex items-center justify-between px-4 py-2.5" data-testid={`serv-item-${s.id}`}>
                <div className="text-sm">
                  <span className="font-medium text-slate-700">{s.nome}</span>
                  <span className={`ml-2 text-xs px-2 py-0.5 rounded ${s.recorrente ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{s.recorrente ? "Recorrente" : `Avulso (${s.cobrarMes || "próxima fatura"})`}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-slate-700">{formatBRL(s.valor)}</span>
                  <button onClick={() => excluirResidenteServico(id, s.id)} className="text-slate-300 hover:text-red-500" data-testid={`serv-del-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Documentos Anexados (administrador e enfermeiro) */}
      {podeDocumentos && (
      <DropZone ativo={podeEnviarDocs && !uploading} onFiles={(fs) => doUploadDrop(fs, "documento")} className="bg-white rounded-xl border border-slate-100 shadow-sm" testId="dropzone-documentos">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h2 className="font-semibold text-slate-800 flex items-center gap-2"><Paperclip className="w-4 h-4 text-blue-600" /> Documentos Anexados</h2>
          <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={handleUpload} />
          {podeEnviarDocs && <Button variant="outline" className="gap-2" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="documento-btn">
            <Upload className="w-4 h-4" /> {uploading ? "Enviando..." : "Enviar arquivos"}
          </Button>}
        </div>
        <div className="p-5">
          <ClasseChips docs={docsGeraisAll} ativo={filtroClasse.documento} onChange={(c) => setFiltroClasse((f) => ({ ...f, documento: c }))} testId="filtro-classe-documento" />
          {uploading && uploadProg && <UploadBar prog={uploadProg} />}
          {docsGerais.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">{filtroClasse.documento ? `Nenhum documento do tipo "${filtroClasse.documento}".` : "Nenhum documento anexado. Envie ou arraste imagens (RG, CPF, exames) ou PDFs para aqui."}</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {docsGerais.map((d) => (
                <div key={d.id} className="rounded-lg border border-slate-100 bg-slate-50/50 overflow-hidden flex flex-col">
                  <DocThumb rid={id} doc={d} fetcher={getDocumentoThumb} onClick={() => handleView(d.id)} tone="bg-blue-50 text-blue-500" />
                  <div className="flex items-center gap-2 px-3 py-2.5">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-700 truncate" title={d.nome}>{d.nome}</p>
                      <p className="text-xs text-slate-400 uppercase flex items-center gap-1.5 flex-wrap">
                        {d.classe && <span className="normal-case inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-700 text-white text-[10px] font-semibold" data-testid={`classe-doc-${d.id}`}><Tag className="w-2.5 h-2.5" />{d.classe}</span>}
                        {d.compartilhado && <span className="normal-case inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[10px] font-semibold" data-testid={`compartilhado-doc-${d.id}`}><Share2 className="w-2.5 h-2.5" />Família</span>}
                        <span>{d.tipo}{d.tamanho ? <span className="normal-case"> · {fmtBytes(d.tamanho)}</span> : null}</span>
                      </p>
                    </div>
                    {podeEditarDocs && <button onClick={() => toggleCompartilhar(d)} title={d.compartilhado ? "Compartilhado com a família (clique para remover)" : "Compartilhar com a família"} className={`p-1 ${d.compartilhado ? "text-emerald-600" : "text-slate-300 hover:text-emerald-600"}`} data-testid={`compartilhar-doc-${d.id}`}><Share2 className="w-4 h-4" /></button>}
                    {podeEditarDocs && <button onClick={() => setDocEdit({ id: d.id, nome: d.nome, classe: d.classe || "", compartilhado: !!d.compartilhado })} title="Renomear / classificar" className="text-slate-400 hover:text-blue-600 p-1" data-testid={`editar-doc-${d.id}`}><Pencil className="w-4 h-4" /></button>}
                    <button onClick={() => handleView(d.id)} title="Abrir" className="text-slate-400 hover:text-blue-600 p-1" data-testid={`ver-doc-${d.id}`}><Eye className="w-4 h-4" /></button>
                    {podeExcluirDocs && <button onClick={() => removerDoc(d.id)} disabled={excluindoDoc === d.id} className="text-slate-400 hover:text-red-600 p-1 disabled:opacity-40" data-testid={`excluir-doc-${d.id}`}><Trash2 className="w-4 h-4" /></button>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DropZone>
      )}

      <Dialog open={!!docEdit} onOpenChange={(o) => !o && setDocEdit(null)}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-md" data-testid="doc-edit-modal">
          <DialogHeader><DialogTitle>Renomear / classificar documento</DialogTitle></DialogHeader>
          {docEdit && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-500">Nome</label>
                <input className="w-full px-3.5 py-2.5 rounded-lg border border-slate-200 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 text-sm" value={docEdit.nome} onChange={(e) => setDocEdit((d) => ({ ...d, nome: e.target.value }))} data-testid="doc-edit-nome" />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-500">Tipo de documento</label>
                <div className="flex flex-wrap gap-1.5">
                  {DOC_CLASSES.map((c) => (
                    <button key={c} type="button" onClick={() => setDocEdit((d) => ({ ...d, classe: d.classe === c ? "" : c }))} className={`text-xs px-2.5 py-1 rounded-full border ${docEdit.classe === c ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`} data-testid={`doc-edit-classe-${c}`}>{c}</button>
                  ))}
                </div>
              </div>
              <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50">
                <input type="checkbox" className="mt-0.5 accent-emerald-600" checked={!!docEdit.compartilhado} onChange={(e) => setDocEdit((d) => ({ ...d, compartilhado: e.target.checked }))} data-testid="doc-edit-compartilhado" />
                <span className="text-sm">
                  <span className="font-medium text-slate-800 flex items-center gap-1.5"><Share2 className="w-3.5 h-3.5 text-emerald-600" /> Compartilhar com a família</span>
                  <span className="block text-xs text-slate-500">O documento aparece na aba Documentos do Portal da Família para os responsáveis vinculados.</span>
                </span>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocEdit(null)}>Cancelar</Button>
            <Button onClick={salvarDocEdit} disabled={salvandoDoc} data-testid="doc-edit-salvar">{salvandoDoc ? "Salvando..." : "Salvar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Arquivo do Paciente (arquivo geral importado de outro sistema) */}
      {podeDocumentos && (
      <DropZone ativo={podeEnviarDocs && !uploadingArq} onFiles={(fs) => doUploadDrop(fs, "arquivo")} className="bg-white rounded-xl border border-slate-100 shadow-sm" testId="arquivo-paciente-section">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h2 className="font-semibold text-slate-800 flex items-center gap-2"><FolderArchive className="w-4 h-4 text-amber-600" /> Arquivo do Paciente</h2>
          <input ref={arqRef} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={handleUploadArquivo} data-testid="arquivo-input" />
          {podeEnviarDocs && <Button variant="outline" className="gap-2" disabled={uploadingArq} onClick={() => arqRef.current?.click()} data-testid="arquivo-btn">
            <FolderArchive className="w-4 h-4" /> {uploadingArq ? "Enviando..." : "Arquivo"}
          </Button>}
        </div>
        <div className="p-5">
          <ClasseChips docs={docsArquivoAll} ativo={filtroClasse.arquivo} onChange={(c) => setFiltroClasse((f) => ({ ...f, arquivo: c }))} testId="filtro-classe-arquivo" />
          {uploadingArq && uploadProg && <UploadBar prog={uploadProg} />}
          {docsArquivo.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">{filtroClasse.arquivo ? `Nenhum arquivo do tipo "${filtroClasse.arquivo}".` : "Arquivo geral do paciente (importado de outro sistema). Envie ou arraste várias imagens e PDFs de uma vez."}</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {docsArquivo.map((d) => (
                <div key={d.id} className="rounded-lg border border-slate-100 bg-amber-50/40 overflow-hidden flex flex-col" data-testid={`arquivo-item-${d.id}`}>
                  <DocThumb rid={id} doc={d} fetcher={getDocumentoThumb} onClick={() => handleView(d.id)} tone="bg-amber-100 text-amber-600" />
                  <div className="flex items-center gap-2 px-3 py-2.5">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-700 truncate" title={d.nome}>{d.nome}</p>
                      <p className="text-xs text-slate-400 uppercase flex items-center gap-1.5 flex-wrap">
                        {d.classe && <span className="normal-case inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-700 text-white text-[10px] font-semibold" data-testid={`classe-doc-${d.id}`}><Tag className="w-2.5 h-2.5" />{d.classe}</span>}
                        {d.compartilhado && <span className="normal-case inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[10px] font-semibold" data-testid={`compartilhado-doc-${d.id}`}><Share2 className="w-2.5 h-2.5" />Família</span>}
                        <span>{d.tipo}{d.tamanho ? <span className="normal-case"> · {fmtBytes(d.tamanho)}</span> : null}</span>
                      </p>
                    </div>
                    {podeEditarDocs && <button onClick={() => toggleCompartilhar(d)} title={d.compartilhado ? "Compartilhado com a família (clique para remover)" : "Compartilhar com a família"} className={`p-1 ${d.compartilhado ? "text-emerald-600" : "text-slate-300 hover:text-emerald-600"}`} data-testid={`compartilhar-doc-${d.id}`}><Share2 className="w-4 h-4" /></button>}
                    {podeEditarDocs && <button onClick={() => setDocEdit({ id: d.id, nome: d.nome, classe: d.classe || "", compartilhado: !!d.compartilhado })} title="Renomear / classificar" className="text-slate-400 hover:text-blue-600 p-1" data-testid={`editar-doc-${d.id}`}><Pencil className="w-4 h-4" /></button>}
                    <button onClick={() => handleView(d.id)} title="Abrir" className="text-slate-400 hover:text-blue-600 p-1" data-testid={`ver-doc-${d.id}`}><Eye className="w-4 h-4" /></button>
                    {podeExcluirDocs && <button onClick={() => removerDoc(d.id)} disabled={excluindoDoc === d.id} className="text-slate-400 hover:text-red-600 p-1 disabled:opacity-40" data-testid={`excluir-doc-${d.id}`}><Trash2 className="w-4 h-4" /></button>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DropZone>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Estoque familiar */}
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm">
          <div className="px-5 py-4 border-b border-slate-100">
            <h2 className="font-semibold text-slate-800 flex items-center gap-2"><Home className="w-4 h-4 text-emerald-600" /> Estoque Familiar</h2>
          </div>
          <div className="p-3 space-y-2">
            {itensEstoque.length === 0 && <p className="text-sm text-slate-400 text-center py-6">Sem produtos no estoque familiar.</p>}
            {itensEstoque.map(([pid, qty]) => {
              const prod = getProduto(pid);
              return (
                <div key={pid} className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-emerald-50/50">
                  <span className="text-sm font-medium text-slate-700">{prod?.nome}</span>
                  <span className="text-sm font-bold text-emerald-700">{qty} {prod?.unidade}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Consumos */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-100 shadow-sm">
          <div className="px-5 py-4 border-b border-slate-100">
            <h2 className="font-semibold text-slate-800 flex items-center gap-2"><ClipboardList className="w-4 h-4 text-blue-600" /> Consumos Recentes</h2>
          </div>
          <div className="divide-y divide-slate-50 max-h-80 overflow-y-auto">
            {consumosRes.length === 0 && <p className="text-sm text-slate-400 text-center py-6">Nenhum consumo registrado.</p>}
            {consumosRes.map((c) => (
              <div key={c.id} className="flex items-center justify-between px-5 py-3">
                <div>
                  <p className="text-sm font-medium text-slate-700">{getProduto(c.produtoId)?.nome}</p>
                  <p className="text-xs text-slate-400">{formatDate(c.data)} · {c.quantidade} un</p>
                </div>
                <div className="flex items-center gap-2">
                  {c.qtdResidente > 0 && <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">Fam. {c.qtdResidente}</span>}
                  {c.qtdEmpresa > 0 && <span className="text-xs font-medium text-violet-700 bg-violet-50 px-2 py-0.5 rounded">Emp. {c.qtdEmpresa}{isAdmin ? ` · ${formatBRL(c.valorCobrado)}` : ""}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Fornecimentos */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm">
        <div className="px-5 py-4 border-b border-slate-100">
          <h2 className="font-semibold text-slate-800 flex items-center gap-2"><PackagePlus className="w-4 h-4 text-blue-600" /> Fornecimentos Familiares</h2>
        </div>
        <div className="divide-y divide-slate-50">
          {fornRes.length === 0 && <p className="text-sm text-slate-400 text-center py-6">Nenhum fornecimento registrado.</p>}
          {fornRes.map((f) => (
            <div key={f.id} className="flex items-center justify-between px-5 py-3">
              <div>
                <p className="text-sm font-medium text-slate-700">{getProduto(f.produtoId)?.nome}</p>
                <p className="text-xs text-slate-400">{formatDate(f.data)} · por {f.entreguePor}</p>
              </div>
              <span className="text-sm font-semibold text-blue-600">+{f.quantidade}</span>
            </div>
          ))}
        </div>
      </div>

      <ResidenteFormDialog open={edit} onOpenChange={setEdit} onSave={salvarResidente} initial={res} />

      {/* Diálogo período do relatório clínico */}
      <Dialog open={relClinicoOpen} onOpenChange={setRelClinicoOpen}>
        <DialogContent aria-describedby={undefined} data-testid="relatorio-periodo-modal">
          <DialogHeader>
            <DialogTitle>Relatório Clínico</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">Escolha o período das anotações e sinais vitais. Deixe em branco para incluir tudo.</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-slate-600 mb-1 block">De</label>
                <input type="date" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500" value={relInicio} onChange={(e) => setRelInicio(e.target.value)} data-testid="rel-inicio" />
              </div>
              <div>
                <label className="text-sm text-slate-600 mb-1 block">Até</label>
                <input type="date" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500" value={relFim} onChange={(e) => setRelFim(e.target.value)} data-testid="rel-fim" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRelInicio(""); setRelFim(""); }}>Limpar</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" disabled={gerando} onClick={handleRelatorioCompleto} data-testid="gerar-relatorio-clinico-btn">
              {gerando ? "Gerando..." : "Gerar PDF"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de inativação (somente admin) */}
      <Dialog open={inativarOpen} onOpenChange={setInativarOpen}>
        <DialogContent aria-describedby={undefined} data-testid="inativar-modal">
          <DialogHeader>
            <DialogTitle>Inativar residente</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">
              O histórico será mantido. Informe o motivo da inativação (obrigatório).
            </p>
            <textarea
              value={motivoInativar}
              onChange={(e) => setMotivoInativar(e.target.value)}
              rows={3}
              placeholder="Ex.: Óbito, transferência, alta a pedido da família..."
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-red-100 focus:border-red-300"
              data-testid="motivo-inativacao-input"
            />
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Data de inativação</label>
              <input
                type="date"
                value={dataInativar}
                onChange={(e) => setDataInativar(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-red-100 focus:border-red-300"
                data-testid="data-inativacao-input"
              />
            </div>
            <label className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 cursor-pointer">
              <input
                type="checkbox"
                checked={gerarFaturaRescisao}
                onChange={(e) => setGerarFaturaRescisao(e.target.checked)}
                className="mt-0.5"
                data-testid="gerar-rescisao-checkbox"
              />
              <span className="text-sm text-slate-600">
                <b>Gerar fatura de rescisão</b> — inclui o <b>13º proporcional</b> (meses do ano até a data de inativação)
                e os <b>consumos ainda não pagos</b> até essa data. A fatura fica no mês da inativação para você revisar (sem emitir boleto automático).
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInativarOpen(false)}>Cancelar</Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={!motivoInativar.trim() || inativando}
              onClick={confirmarInativar}
              data-testid="confirmar-inativar-btn"
            >
              {inativando ? "Processando..." : "Inativar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={contratoOpen} onOpenChange={setContratoOpen}>
        <DialogContent aria-describedby={undefined} data-testid="enviar-contrato-modal">
          <DialogHeader>
            <DialogTitle>Enviar contrato por e-mail</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">
              O contrato de <b>{res.nome}</b> será gerado em PDF e enviado ao e-mail informado.
            </p>
            <input
              type="email"
              value={contratoEmail}
              onChange={(e) => setContratoEmail(e.target.value)}
              placeholder="email@exemplo.com"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
              data-testid="contrato-email-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setContratoOpen(false)}>Cancelar</Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              disabled={!contratoEmail.trim() || enviandoContrato}
              onClick={handleEnviarContrato}
              data-testid="confirmar-enviar-contrato-btn"
            >
              {enviandoContrato ? "Enviando..." : "Enviar contrato"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!emailDoc} onOpenChange={(o) => !o && setEmailDoc(null)}>
        <DialogContent aria-describedby={undefined} data-testid="enviar-documento-modal">
          <DialogHeader>
            <DialogTitle>Enviar {emailDoc?.label} por e-mail</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">
              O documento <b>{emailDoc?.label}</b> de <b>{res.nome}</b> será gerado em PDF e enviado ao e-mail informado.
            </p>
            <input
              type="email"
              value={emailDocValor}
              onChange={(e) => setEmailDocValor(e.target.value)}
              placeholder="email@exemplo.com"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
              data-testid="documento-email-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDoc(null)}>Cancelar</Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              disabled={!emailDocValor.trim() || enviandoDoc}
              onClick={handleEnviarDoc}
              data-testid="confirmar-enviar-documento-btn"
            >
              {enviandoDoc ? "Enviando..." : "Enviar documento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResidenteDetalhe;
